import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import _ from 'lodash';
import Header from 'quill/formats/header';
import { useDebouncedCallback } from 'use-debounce';
import type { Delta } from 'quill/core';
import Quill from 'quill/core';
import type { EmitterSource } from 'quill/core/emitter';
import { useApolloClient } from '@apollo/client';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import { isFeatureEnabled } from '../utils/features';
import UIActions from '../ui/UIActions';
import { usePostTOC } from './PostTOCContext';
import { useQuill } from '../editor/QuillContext';
import type { DocumentType } from '../graphql/generated';
import { gql } from '../graphql/generated';

type PostLayoutSize =
  | 'mobile'
  | 'narrow'
  | 'small'
  | 'regular'
  | 'wide'
  | 'xWide';

const PostLayoutSizeContext = React.createContext<PostLayoutSize>('mobile');

interface Breakpoints {
  narrow: number;
  small: number;
  regular: number;
  wide: number;
  xWide: number;
}

function getPostLayoutSize(
  currentSize: number,
  { narrow, small, regular, wide, xWide }: Breakpoints,
): PostLayoutSize {
  if (currentSize < narrow) return 'mobile';
  if (currentSize < small) return 'narrow';
  if (currentSize < regular) return 'small';
  if (currentSize < wide) return 'regular';
  if (currentSize < xWide) return 'wide';
  return 'xWide';
}

interface LegacyPostLayoutSizeProviderProps {
  children: React.ReactNode;
}

// The default implementation is replicating the layout sizes from post.scss
// If you change the breakpoints here, also change them in post.scss
export const LegacyPostLayoutSizeProvider = ({
  children,
}: LegacyPostLayoutSizeProviderProps) => {
  const [currentSize, setCurrentSize] = useState(() => window.innerWidth);
  const isSidebarOpen = useAppSelector((state) => state.ui.isSidebarOpen);
  const isRightPanelOpen = useAppSelector((state) => state.ui.rightPanel.open);

  useEffect(() => {
    const handleResize = () => {
      setCurrentSize(window.innerWidth);
    };

    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const postLayoutSize = useMemo(() => {
    if (isRightPanelOpen && isSidebarOpen) {
      return getPostLayoutSize(currentSize, {
        narrow: 768,
        small: 1280,
        regular: 1440,
        wide: 1600,
        xWide: 1920,
      });
    }

    if (isSidebarOpen || isRightPanelOpen) {
      return getPostLayoutSize(currentSize, {
        narrow: 768,
        small: 992,
        regular: 1280,
        wide: 1440,
        xWide: 1600,
      });
    }

    return getPostLayoutSize(currentSize, {
      narrow: 768,
      small: 768,
      regular: 1024,
      wide: 1280,
      xWide: 1440,
    });
  }, [isRightPanelOpen, isSidebarOpen, currentSize]);

  return (
    <PostLayoutSizeContext.Provider value={postLayoutSize}>
      {children}
    </PostLayoutSizeContext.Provider>
  );
};

type ComputedPostLayoutState = {
  isTOCExpanded: boolean;
  canTOCExpand: boolean;
  canMagneticWallExpand: boolean;
};

const PostLayoutStateContext = React.createContext<ComputedPostLayoutState>({
  isTOCExpanded: false,
  canTOCExpand: false,
  canMagneticWallExpand: false,
});

function getPostLayoutSizeFromWidth(width: number): PostLayoutSize {
  if (width >= 1486) return 'xWide';
  if (width >= 1326) return 'wide';
  if (width >= 1280) return 'regular';
  if (width >= 1076) return 'small';
  if (width >= 916) return 'narrow';
  return 'mobile';
}

const useObservedPostLayoutSize = () => {
  const [postLayoutSize, setPostLayoutSize] = useState<PostLayoutSize>(() =>
    getPostLayoutSizeFromWidth(window.innerWidth),
  );

  useEffect(() => {
    setPostLayoutSize(getPostLayoutSizeFromWidth(window.innerWidth));

    const handleResize = () => {
      setPostLayoutSize(getPostLayoutSizeFromWidth(window.innerWidth));
    };

    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return postLayoutSize;
};

type PostLayoutApi = {
  toggleTOC(): void;
  expandMagneticWall(): void;
  triggerMagneticWallCollapseAction(): void;
};

const TYPING_DEBOUNCE_TIMEOUT = 3000;

const PostLayoutApiContext = React.createContext<PostLayoutApi>({
  toggleTOC: _.noop,
  expandMagneticWall: _.noop,
  triggerMagneticWallCollapseAction: _.noop,
});

type Sidebar = 'sidebar';
type Area = 'magneticWall' | 'toc';
type PriorityList =
  | [Area, Area]
  | [Sidebar, Area, Area]
  | [Area, Sidebar, Area];

function hasSidebar(
  priorityList: PriorityList,
): priorityList is [Sidebar, Area, Area] | [Area, Sidebar, Area] {
  return priorityList.length === 3 && priorityList.includes('sidebar');
}

const POST_FRAGMENT = gql(`
  fragment PostLayoutProvider_post on RootPostFields {
    id
  }
`);

const usePriorityList = ({
  postLayoutSize,
  hasMagneticWallItems,
}: {
  postLayoutSize: PostLayoutSize;
  hasMagneticWallItems: boolean;
}) => {
  const { quill } = useQuill();
  const { toggleTOC } = usePostTOC();
  const dispatch = useAppDispatch();
  const isSidebarOpen = useAppSelector((state) => state.ui.isSidebarOpen);
  const shouldPrioritizeAreas =
    (postLayoutSize === 'regular' && (hasMagneticWallItems || isSidebarOpen)) ||
    (postLayoutSize === 'wide' && isSidebarOpen && hasMagneticWallItems);

  const [priorityList, setPriorityList] = useState<PriorityList>(() =>
    isSidebarOpen
      ? ['sidebar', 'toc', 'magneticWall']
      : ['toc', 'magneticWall'],
  );

  useEffect(() => {
    if (isSidebarOpen) {
      setPriorityList((current) =>
        hasSidebar(current) ? current : ['sidebar', ...current],
      );
    } else {
      setPriorityList((current) =>
        hasSidebar(current)
          ? (current.filter((p) => p !== 'sidebar') as PriorityList)
          : current,
      );
    }
  }, [isSidebarOpen]);

  useEffect(() => {
    const [first, second] = priorityList;

    const shouldOpenSidebar =
      first === 'sidebar' ||
      (second === 'sidebar' && postLayoutSize === 'wide');
    dispatch(UIActions.toggleSidebar(shouldOpenSidebar));
  }, [dispatch, postLayoutSize, priorityList]);

  useEffect(() => {
    const [first, second] = priorityList;

    const hasHeaders = !!quill && quill.scroll.descendants(Header).length > 0;
    const shouldOpenTOC =
      hasHeaders &&
      (first === 'toc' ||
        (second === 'toc' &&
          (postLayoutSize === 'wide' || postLayoutSize === 'xWide')));

    toggleTOC(shouldOpenTOC);
  }, [toggleTOC, postLayoutSize, priorityList, quill]);

  const prioritizeArea = useCallback(
    (area: Area) => {
      if (!shouldPrioritizeAreas) return;
      setPriorityList((current) => {
        const next = current.filter((a) => a !== area);
        next.unshift(area);
        return next as PriorityList;
      });
    },
    [shouldPrioritizeAreas],
  );

  const deprioritizeArea = useCallback(
    (area: Area) => {
      if (!shouldPrioritizeAreas) return;
      setPriorityList((current) => {
        const next = current.filter((a) => a !== area);
        next.push(area);
        return next as PriorityList;
      });
    },
    [shouldPrioritizeAreas],
  );

  return {
    prioritizeArea,
    deprioritizeArea,
  };
};

interface NewPostLayoutSizeProviderProps {
  children: React.ReactNode;
  post: DocumentType<typeof POST_FRAGMENT> | undefined;
  hasMagneticWallItems: boolean;
}

const NewPostLayoutSizeProvider = ({
  children,
  post,
  hasMagneticWallItems,
}: NewPostLayoutSizeProviderProps) => {
  const client = useApolloClient();
  const { quill } = useQuill();
  const { isTOCOpen, toggleTOC } = usePostTOC();

  const isSidebarOpen = useAppSelector((state) => state.ui.isSidebarOpen);

  const postLayoutSize = useObservedPostLayoutSize();
  const canTOCExpand = isSidebarOpen
    ? !['mobile', 'narrow', 'small'].includes(postLayoutSize)
    : !['mobile', 'narrow'].includes(postLayoutSize);
  const canMagneticWallExpand = !['mobile', 'narrow', 'small'].includes(
    postLayoutSize,
  );

  const { prioritizeArea, deprioritizeArea } = usePriorityList({
    postLayoutSize,
    hasMagneticWallItems,
  });

  const postLayoutApi = useMemo<PostLayoutApi>(
    () => ({
      toggleTOC: () => {
        const isNextTOCOpen = !isTOCOpen;
        if (isNextTOCOpen) {
          prioritizeArea('toc');
        } else {
          deprioritizeArea('toc');
        }

        toggleTOC();
      },
      expandMagneticWall: () => {
        prioritizeArea('magneticWall');
      },
      triggerMagneticWallCollapseAction: () => {
        if (!post) return;
        const cachedPost = client.readFragment({
          id: client.cache.identify(post),
          fragment: gql(`
            fragment PostLayoutProvider_magneticWallItems on RootPostFields {
              id
              ... on PublicPostFields {
                threads {
                  id
                }
                annotations {
                  id
                }
              }
            }
          `),
        });
        const shouldCollapseMagneticWall =
          (cachedPost?.__typename === 'Post' ||
            cachedPost?.__typename === 'PublicPost') &&
          cachedPost.threads.length === 0 &&
          cachedPost.annotations.length === 0;

        if (!shouldCollapseMagneticWall) return;

        deprioritizeArea('magneticWall');
      },
    }),
    [post, isTOCOpen, client, toggleTOC, prioritizeArea, deprioritizeArea],
  );

  const magneticWallFocus = useAppSelector(
    (state) => state.editor.magneticWallFocus,
  );

  useEffect(() => {
    if (magneticWallFocus?.hasExpandIntent && canMagneticWallExpand) {
      postLayoutApi.expandMagneticWall();
    }
  }, [
    postLayoutApi,
    magneticWallFocus?.hasExpandIntent,
    canMagneticWallExpand,
  ]);

  const prevHasHeadingsRef = useRef(false);
  const debounceUpdateHasHeadings = useDebouncedCallback(() => {
    if (!quill) return;

    const hasHeadings = quill.scroll.descendants(Header).length > 0;

    if (!prevHasHeadingsRef.current && hasHeadings) {
      toggleTOC(true);
      prioritizeArea('toc');
    } else if (prevHasHeadingsRef.current && !hasHeadings) {
      toggleTOC(false);
      deprioritizeArea('toc');
    }

    prevHasHeadingsRef.current = hasHeadings;
  }, TYPING_DEBOUNCE_TIMEOUT);

  useEffect(() => {
    if (!quill) return undefined;

    prevHasHeadingsRef.current = quill.scroll.descendants(Header).length > 0;

    const onTextChange = (
      _delta: Delta,
      _old: Delta,
      source: EmitterSource,
    ) => {
      if (source === 'user') {
        debounceUpdateHasHeadings.callback();
      }
    };

    quill.on(Quill.events.TEXT_CHANGE, onTextChange);

    return () => {
      quill.off(Quill.events.TEXT_CHANGE, onTextChange);
      debounceUpdateHasHeadings.cancel();
    };
  }, [toggleTOC, quill, debounceUpdateHasHeadings]);

  const computedPostLayoutState = useMemo<ComputedPostLayoutState>(() => {
    return {
      isTOCExpanded: canTOCExpand ? isTOCOpen : false,
      canTOCExpand,
      canMagneticWallExpand,
    };
  }, [canTOCExpand, isTOCOpen, canMagneticWallExpand]);

  return (
    <PostLayoutApiContext.Provider value={postLayoutApi}>
      <PostLayoutStateContext.Provider value={computedPostLayoutState}>
        <PostLayoutSizeContext.Provider value={postLayoutSize}>
          {children}
        </PostLayoutSizeContext.Provider>
      </PostLayoutStateContext.Provider>
    </PostLayoutApiContext.Provider>
  );
};

export const usePostLayoutSize = () => useContext(PostLayoutSizeContext);
export const usePostLayoutState = () => useContext(PostLayoutStateContext);
export const usePostLayoutApi = () => useContext(PostLayoutApiContext);

const PostLayoutProvider = ({
  children,
  post,
  hasMagneticWallItems,
}: NewPostLayoutSizeProviderProps) => {
  if (isFeatureEnabled('new_post_layout')) {
    return (
      <NewPostLayoutSizeProvider
        hasMagneticWallItems={hasMagneticWallItems}
        post={post}
      >
        {children}
      </NewPostLayoutSizeProvider>
    );
  }

  return (
    <LegacyPostLayoutSizeProvider>{children}</LegacyPostLayoutSizeProvider>
  );
};

export default PostLayoutProvider;
